import React, { useState, FormEvent, useEffect } from 'react';
import { apiCall } from '../../api/services';  // Adjust the path as needed
import useAuth from '../../hooks/useAuth';
import styles from "./Login.module.css";
import { Button, Image, Input, Link, Persona, ProgressBar } from '@fluentui/react-components';
import { ArrowLeftRegular, ChevronRightRegular, EyeOffRegular, EyeRegular } from '@fluentui/react-icons';
import { ErrorMessage } from '../../constants/ErrorMessage/ErrorMessage';
import { ActiveUser, ActiveUserCredentials } from '../../types/InitialCredentials';
import companyLogo from '../../assets/images/CPN.svg';
import Shadows from '../../constants/Loaders/shadow';

interface LoginPageProps {
  loginMethod: 'Locked' | 'Initial' | null;
}

const Login: React.FC<LoginPageProps> = ({ loginMethod }) => {
  const { login, user } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [validated, setValidated] = useState(false);
  const [errorTitle, setErrorTitle] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isError, setIsError] = useState<boolean>(false);
  const [isInProgress, setIsInProgress] = useState<boolean>(false);
  const [initialLoginCredentials, setInitialLoginCredentials] =
    useState<ActiveUserCredentials>(ActiveUser);
  const [currentLoginMethod, setCurrentLoginMethod] =
    useState<LoginPageProps['loginMethod']>(loginMethod);

  // Locked mode: the email is already known from the signed-in user
  useEffect(() => {
    if (user && currentLoginMethod === 'Locked') {
      setEmail(user.email);
    }
  }, [user, currentLoginMethod]);

  useEffect(() => {
    if (loginMethod === null) {
      setCurrentLoginMethod('Initial');
    }
  }, [loginMethod]);

  // Step 1 = identifier (email/phone), Step 2 = password
  const showEmailStep = !validated && currentLoginMethod !== 'Locked';
  const buttonLabel = showEmailStep ? 'Next' : 'Login';
  const hasProfile = !!(initialLoginCredentials.firstName && initialLoginCredentials.lastName);

  const showError = (title: string, message: string) => {
    setErrorTitle(title);
    setErrorMessage(message);
    setIsError(true);
  };

  const closeIsError = () => setIsError(false);

  const goBackToEmail = () => {
    setValidated(false);
    setPassword('');
    setShowPassword(false);
    setIsError(false);
    setInitialLoginCredentials(ActiveUser);
    setCurrentLoginMethod('Initial');
  };

  const handleEmailSubmit = async () => {
    const identifier = email.trim();
    if (!identifier) {
      showError('Error: ', 'Enter your email or phone');
      return;
    }

    try {
      const userData = await apiCall({
        service: 'auth',
        endpoint: `Auth/getuser?email=${encodeURIComponent(identifier)}`,
        method: 'GET'
      });

      if (!userData || userData.error) {
        showError('Error: ', `User with this id ${identifier} does not exist`);
      } else if (userData.tenantStatus === false) {
        showError('Error: ', `${userData.tenantName}'s subscription has expired`);
      } else {
        setInitialLoginCredentials(userData);
        setValidated(true);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      showError('Error: ', `${(error as Error)?.message ?? error} validating`);
    }
  };

  const handlePasswordSubmit = async () => {
    if (!password) {
      showError('Error: ', 'Enter your password');
      return;
    }

    try {
      await login(email, password);
    } catch (error) {
      showError('Failed: ', `${(error as Error)?.message ?? error} Invalid login credentials`);
    }
  };

  const handleSubmit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (isInProgress) return;

    setIsError(false);
    setIsInProgress(true);
    try {
      if (showEmailStep) {
        await handleEmailSubmit();
      } else {
        await handlePasswordSubmit();
      }
    } finally {
      setIsInProgress(false);
    }
  };

  return (
    <>
      <div className={styles.loginPage}>
        <div className={styles.loginCard}>
          <div className={styles.loginProgress}>
            {isInProgress && <ProgressBar />}
          </div>

          <div className={styles.loginLogo}>
            <Image src={companyLogo} alt="Cilpron" height={44} width={44} />
          </div>

          <h1 className={styles.loginTitle}>Sign in</h1>
          <p className={styles.loginSubtitle}>
            Using your Cilpron Account, you can access other Cilpron apps directly within the same software.
          </p>

          <form className={styles.loginForm} onSubmit={handleSubmit} noValidate>
            {/* Identified user chip (step 2) */}
            {!showEmailStep && (
              <div className={styles.loginUser}>
                {hasProfile ? (
                  <Persona
                    size="large"
                    name={`${initialLoginCredentials.firstName} ${initialLoginCredentials.lastName}`}
                    secondaryText={email}
                    presence={{ status: 'available' }}
                    avatar={{ color: 'colorful' }}
                  />
                ) : (
                  <span className={styles.loginUserEmail}>{email}</span>
                )}
                {currentLoginMethod !== 'Locked' && (
                  <Button
                    type="button"
                    appearance="subtle"
                    size="small"
                    icon={<ArrowLeftRegular />}
                    onClick={goBackToEmail}
                    aria-label="Use a different account"
                  />
                )}
              </div>
            )}

            {isError && (
              <div className={styles.loginError} role="alert" aria-live="assertive">
                <ErrorMessage
                  errorTitle={errorTitle}
                  errorMessages={errorMessage}
                  closeFunction={closeIsError}
                />
              </div>
            )}

            <div className={styles.loginField}>
              {showEmailStep ? (
                <Input
                  key="identifier"
                  className={styles.loginInput}
                  size="large"
                  type="text"
                  inputMode="email"
                  name="username"
                  autoComplete="username"
                  placeholder="Email or phone"
                  aria-label="Email or phone"
                  autoFocus
                  value={email}
                  onChange={(_, data) => setEmail(data.value)}
                />
              ) : (
                <Input
                  key="password"
                  className={styles.loginInput}
                  size="large"
                  name="password"
                  autoComplete="current-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Password"
                  aria-label="Password"
                  autoFocus
                  value={password}
                  onChange={(_, data) => setPassword(data.value)}
                  contentAfter={
                    <Button
                      type="button"
                      appearance="transparent"
                      size="small"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      icon={showPassword ? <EyeOffRegular /> : <EyeRegular />}
                      onClick={() => setShowPassword((s) => !s)}
                    />
                  }
                />
              )}
            </div>

            <div className={styles.loginLinks}>
              <Link as="button" type="button">Forgot credentials?</Link>
            </div>

            <div className={styles.loginActions}>
              <Button type="button" appearance="transparent" shape="circular">
                Request an account
              </Button>
              <Button
                type="submit"
                appearance="primary"
                shape="circular"
                size="large"
                disabled={isInProgress}
                icon={<ChevronRightRegular />}
                iconPosition="after"
                className={styles.loginPrimaryBtn}
              >
                {buttonLabel}
              </Button>
            </div>
          </form>

          <p className={styles.loginFooterNote}>
            Not your computer? Use one time password mode to sign in privately.{' '}
            <Link as="button" type="button">Learn more about using one time password mode</Link>
          </p>
        </div>
      </div>

      {isInProgress && <Shadows />}
    </>
  );
};

export default Login;